import { useCallback } from 'react';
import { StrictDict, useKeyedState } from '@edx/react-unit-test-utils';
import { sendTrackEvent, sendTrackingLogEvent } from '@edx/frontend-platform/analytics';
import { getCookies } from '../../../utils/cookie';

export const stateKeys = StrictDict({
  selectedIndex: 'selectedIndex',
});

const logUpgradeClick = ({ logEventProperties }) => {
  const eventProperties = {
    org_key: logEventProperties.org_key,
    courserun_key: logEventProperties.courserun_key,
  };
  const upsellPageName = 'course_translation_modal';
  const promotionEventProperties = {
    creative: 'translationmodalupsell',
    name: 'In-Course Verification Prompt',
    position: 'translation-modal-message',
    promotion_id: 'courseware_translation_upsell',
    ...eventProperties,
  };
  sendTrackingLogEvent('edx.bi.course.upgrade.translationmodal.clicked', eventProperties);
  sendTrackingLogEvent('edx.course.enrollment.upgrade.clicked', {
    ...eventProperties,
    location: 'translation-modal-message',
  });
  sendTrackEvent('Promotion Clicked', promotionEventProperties);
  sendTrackEvent('edx.bi.ecommerce.upsell_links_clicked', {
    ...eventProperties,
    linkCategory: 'green_upgrade',
    linkName: `${upsellPageName}_green`,
    linkType: 'button',
    pageName: upsellPageName,
  });

  const correlationId = getCookies()?.tglr_correlation_id;
  window.tagular?.('beam', {
    '@type': 'core.conversions.ConversionTracked.v2',
    correlation: {
      id: correlationId,
    },
    metadata: {
      category: 'Enrollment',
      action: 'Upgraded',
      timestamp: new Date().toISOString(),
      productList: [{
        brand: logEventProperties.org_key?.toLowerCase(),
        variant: 'courses',
        name: logEventProperties.course_name,
      }],
    },
  });
};

const getEnabledLanguages = ({ availableLanguages, originalCourseLanguage }) => {
  let enabledLanguages = [];
  const excludeEnglish = originalCourseLanguage !== 'en';
  const missingLanguage = !availableLanguages.some(({ code }) => code === originalCourseLanguage);

  enabledLanguages = availableLanguages.filter(({ enabled, code }) => {
    if (excludeEnglish && code === 'en') {
      return false;
    }

    return enabled;
  });

  if (missingLanguage) {
    enabledLanguages.push({ code: originalCourseLanguage, label: 'original' });
  }

  return enabledLanguages;
};

const useTranslationModal = ({
  selectedLanguage,
  setSelectedLanguage,
  close,
  isAuditMode,
  upgradeUrl,
  logEventProperties,
  availableLanguages,
  originalCourseLanguage,
}) => {
  const courseEnabledLanguages = getEnabledLanguages({ availableLanguages, originalCourseLanguage });
  const [selectedIndex, setSelectedIndex] = useKeyedState(
    stateKeys.selectedIndex,
    courseEnabledLanguages.findIndex((lang) => lang.code === selectedLanguage),
  );

  const onSubmit = useCallback(() => {
    if (isAuditMode) {
      logUpgradeClick({ logEventProperties });
      window.open(upgradeUrl, '_self');
    } else {
      const newSelectedLanguage = courseEnabledLanguages[selectedIndex].code;
      setSelectedLanguage(newSelectedLanguage);
    }
    close();
  }, [selectedIndex]);

  /*
    2025-01-21
    This function not currently in use, but may be used again in the future so not removing the code here.
    The import and pasing of this function have been removed except in `./NewLanguageFeedback.jsx` where it
    is actually used.
  */
  const logSurveyClick = () => {
    sendTrackEvent('addtional_languages_survey_clicked', {
      ...logEventProperties,
      isAuditUser: isAuditMode,
    });
    window.open('https://participant.use2.usertesting.com/se/invite/8145e5f3-4f48-46b7-8d97-78e1bec900d0', '_blank');
  };

  return {
    selectedIndex,
    setSelectedIndex,
    onSubmit,
    logSurveyClick,
    courseEnabledLanguages,
  };
};

export default useTranslationModal;
